<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import Header from '@/components/common/Header.vue';
import LanguageUpdater from '@/components/common/LanguageUpdater.vue';
import { useSnackbar } from '@/composables/useSnackbar';
import { useAppStore } from '@/stores/app';
import { useUserStore } from '@/stores/user';
import { useWebSocket } from '@/composables/useWebSocket';
import { startBrandKitNotices, stopBrandKitNotices } from '@/composables/brandKitExtractionNotice';
import { fetchContextOptions } from '@/composables/useContextOptions';
import axios from '@/plugins/axios';
import { getDocumentLang } from '@/utils/documentLang';
import LegalDocumentsDialog from '@/components/common/LegalDocumentsDialog.vue';
import { MaintenanceOverlay } from '@/design-system/components/organisms';
import { useAnalytics } from '@/composables/useAnalytics';
import { PUBLIC_REPORT_ROUTE_NAME, isPublicReportPath } from '@/utils/publicReportRoute';

const { t } = useTranslation();

const route = useRoute();
const { snackbarState, hideSnackbar, showSnackbar, runSnackbarAction } = useSnackbar();
const { identify, setUserProfile, registerSuperProperties, unregisterSuperProperty, reset: resetAnalytics } = useAnalytics();
const appStore = useAppStore();
const userStore = useUserStore();

const termsUpdateDialog = ref({
    show: false,
    content: '',
    version: ''
});
const isLoadingTermsContent = ref(false);

// WebSocket — all connection logic is now in the composable
const { open: openWebSocket, close: closeWebSocket } = useWebSocket();

const showMainHeader = computed(() => {
    // SYN-1519: FirstAccess traz o próprio casco (gradiente + logo + rodapé legal),
    // como o login — o header global renderizaria por cima dela.
    // SYN-1548: ResetPassword entrou na lista ao ganhar o mesmo casco. Antes ela
    // era a única tela pública de senha com o header global sobreposto.
    // SYN-1689: CompareView é tela cheia como a do criativo — barra superior
    // própria (voltar + título + toggle de mapa).
    // SYN-2721: o relatório público tem a própria barra, e o visitante não tem conta.
    const namedRoutesWithoutHeader = ['ExperimentView', 'FirstAccess', 'ResetPassword', 'TermsOfUse', 'PrivacyPolicy', 'SubprocessorsPolicy', 'CompareView',
        'TermsOfUseLegacy', 'PrivacyPolicyLegacy', 'SubprocessorsPolicyLegacy', PUBLIC_REPORT_ROUTE_NAME];
    if (namedRoutesWithoutHeader.includes(route.name)) {
        return false;
    }
    // Hide header for landing page, login, and main view (which has its own sidebar)
    const pathsWithoutHeader = ['/', '/login'];
    if (pathsWithoutHeader.includes(route.path)) {
        return false;
    }
    // Main view has its own AppSidebar with header functionality
    if (route.path.startsWith('/main')) {
        return false;
    }
    return true;
});

const mainPaddingTop = computed(() => {
    if (appStore.isInMaintenanceMode) return '0px';
    // Main view handles its own layout with the new sidebar
    if (route.path.startsWith('/main')) {
        return '0px';
    }
    if (showMainHeader.value) {
        return userStore.isImpersonating ? '0px' : '80px';
    }
    return '0px';
});

const attemptReload = () => {
    appStore.clearMaintenanceMode();
    window.location.reload();
};

async function reviewUpdatedTerms() {
    if (!userStore.newTermsVersionToAcknowledge) return;
    isLoadingTermsContent.value = true;
    termsUpdateDialog.value.show = true;
    try {
        const response = await axios.get(`/documents/${userStore.termsDocType}/versions/${userStore.newTermsVersionToAcknowledge}`, { params: { lang: getDocumentLang() } });
        termsUpdateDialog.value.content = response.data;
        termsUpdateDialog.value.version = userStore.newTermsVersionToAcknowledge;
    } catch (error) {
        console.error("Error fetching updated terms content:", error);
        showSnackbar(t('views.app.terms.loadError'), 'error');
        termsUpdateDialog.value.show = false;
    } finally {
        isLoadingTermsContent.value = false;
    }
}

function onTermsDialogClosed() {
    termsUpdateDialog.value.show = false;
    userStore.acknowledgeTermsUpdate();
}

function handleKeyDown(e) {
    if (e.ctrlKey || e.metaKey) {
        appStore.triggerSuperAdminFeatures();
    }
}

function handleKeyUp() {
    // Features remain visible for 3 seconds (handled by store timeout)
}

onMounted(() => {
    // SYN-2721: a página pública do relatório não abre sessão nem WebSocket, mesmo
    // com um token no localStorage (pode estar vencido — o 401 jogaria o visitante
    // para a landing por cima do relatório).
    if (userStore.isAuthenticated && !isPublicReportPath()) {
        appStore.fetchNeuralTiers();
        appStore.fetchNeuralTiersVideo();
        appStore.fetchGazeTiers();
        // Catálogo das caixinhas de contexto (config do superadmin, 22/09) —
        // mesma cadência dos tiers: busca no boot autenticado e no login.
        fetchContextOptions();
        openWebSocket();
    }
    // RF-T4.8: a conclusão da extração do Brand DNA avisa mesmo com o painel
    // fechado. O ouvinte tem de viver ACIMA dos dois hosts, que destroem o painel
    // ao fechar, então nasce aqui e vale em qualquer rota. Fora da guarda de
    // autenticação de propósito: sem sessão não há WebSocket e nada chega ao
    // eventBus, e armar aqui evita repetir a chamada no `watch` do login.
    startBrandKitNotices();
    // Listen for Ctrl key press
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    // SYN-2721: a página pública não acompanha nem renova a sessão — um logout em
    // outra aba tiraria o visitante do relatório.
    if (!isPublicReportPath()) {
        // SYN-2755 — login/logout feito em outra aba
        window.addEventListener('storage', userStore.syncFromStorage);
        // SYN-2757 — uso real (clique/tecla) renova o token; requests automáticas não
        window.addEventListener('pointerdown', userStore.renewTokenIfStale);
        window.addEventListener('keydown', userStore.renewTokenIfStale);
    }
});

onUnmounted(() => {
    // WebSocket cleanup is handled automatically by the composable's onUnmounted
    stopBrandKitNotices();
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('keyup', handleKeyUp);
    window.removeEventListener('storage', userStore.syncFromStorage);
    window.removeEventListener('pointerdown', userStore.renewTokenIfStale);
    window.removeEventListener('keydown', userStore.renewTokenIfStale);
});

watch(() => userStore.isAuthenticated, (isAuth) => {
    if (isAuth) {
        appStore.fetchNeuralTiers();
        appStore.fetchNeuralTiersVideo();
        appStore.fetchGazeTiers();
        fetchContextOptions();
        openWebSocket();
    } else {
        closeWebSocket();
        resetAnalytics();
    }
});

// Identificação só com o `userId` já resolvido. No login SSO o `Login.vue` seta o
// token ANTES do `/users/me`, então `isAuthenticated` virava true com um id stale
// (do usuário anterior) ou null — e era esse que ia pro Mixpanel. Reagir ao userId
// também re-registra as super props quando o `/users/me` atualiza role/authProvider.
// A guarda de `isAuthenticated` fecha a janela inversa: um `/users/me` em voo que
// resolve DEPOIS do logout reescreve `userId` no store com o token já zerado — sem
// a guarda, isso re-identificava o usuário antigo sobre o distinct_id anônimo pós-reset.
watch(() => [userStore.userId, userStore.clientId, userStore.role, userStore.authProvider], ([userId, clientId, role, authProvider]) => {
    if (!userId || !userStore.isAuthenticated) return;
    // String(): o store hidrata `userId` do localStorage como string e o /users/me
    // devolve número; sem normalizar, todo F5 gerava dois $identify ("123" → 123).
    identify(String(userId));
    setUserProfile({
        $name: userStore.name,
        $email: userStore.email,
        role,
        clientId,
    });
    registerSuperProperties({
        client_id: clientId,
        role,
        auth_provider: authProvider,
    });
}, { immediate: true });

// Impersonação é flag, não troca de identidade: o distinct_id continua o do
// superadmin. Sem `immediate` porque o estado inicial nunca é "impersonando".
// Observa também o `effectiveClientId` para que trocar de impersonado (A → B
// sem passar por stopImpersonation) re-registre o cliente certo; o `wasOn`
// evita um unregister espúrio quando só o clientId do próprio usuário muda.
watch(() => [userStore.isImpersonating, userStore.effectiveClientId], ([isOn, effectiveClientId], [wasOn]) => {
    if (isOn) {
        registerSuperProperties({
            is_impersonating: true,
            impersonating_client_id: effectiveClientId,
        });
    } else if (wasOn) {
        unregisterSuperProperty('is_impersonating');
        unregisterSuperProperty('impersonating_client_id');
    }
});

</script>

<template>
    <v-app>
        <LanguageUpdater />
        <v-system-bar
            v-if="userStore.isImpersonating"
            color="deep-orange-darken-3"
            height="40"
            class="px-4 d-flex justify-center align-center mp-sensitive"
            style="position: relative;"
        >
            <v-icon icon="mdi-eye-outline" class="mr-2"></v-icon>
            <span class="font-weight-bold mr-2">{{ t('views.app.impersonation.viewingAs') }}</span>
            <span>{{ t('views.app.impersonation.viewingAsUser') }} <strong>{{ userStore.impersonatingUser?.name }}</strong> ({{ userStore.impersonatingUser?.email }}).</span>
            <span class="mx-2">|</span>
            <span class="font-italic mr-4">{{ t('views.app.impersonation.writeActionsDisabled') }}</span>
            <v-btn
                variant="outlined"
                size="small"
                color="white"
                @click="userStore.stopImpersonation"
                prepend-icon="mdi-exit-to-app"
            >
                {{ t('views.app.impersonation.exitView') }}
            </v-btn>
        </v-system-bar>

        <Header
            v-if="showMainHeader && !appStore.isInMaintenanceMode"
            :offset-top="0"
        />

        <v-main :style="{
            paddingTop: mainPaddingTop,
            height: userStore.isImpersonating ? 'calc(100vh - 40px)' : '100vh',
            overflowY: appStore.isInMaintenanceMode ? 'hidden' : 'hidden'
        }">
            <router-view v-if="!appStore.isInMaintenanceMode" style="height: 100%; overflow-y: auto;"></router-view>
        </v-main>

        <v-snackbar
            v-if="!appStore.isInMaintenanceMode"
            v-model="snackbarState.show"
            :color="snackbarState.color"
            :timeout="snackbarState.timeout"
            :location="snackbarState.location"
        >
            <span class="mp-sensitive">{{ snackbarState.message }}</span>
            <template v-slot:actions>
                <v-btn v-if="snackbarState.action" color="white" variant="text" @click="runSnackbarAction">
                    {{ snackbarState.action.label }}
                </v-btn>
                <v-btn color="white" variant="text" @click="hideSnackbar">
                    {{ t('views.app.snackbar.close') }}
                </v-btn>
            </template>
        </v-snackbar>

        <v-snackbar
            v-model="userStore.showTermsUpdateNotice"
            color="info"
            :timeout="-1"
            location="bottom center"
            multi-line
            class="terms-update-snackbar"
        >
            {{ t('views.app.terms.updatedNotice') }}
            <template v-slot:actions>
                <v-btn color="white" variant="text" @click="reviewUpdatedTerms">
                    {{ t('views.app.terms.review') }}
                </v-btn>
                <v-btn color="white" variant="text" @click="userStore.acknowledgeTermsUpdate">
                    {{ t('views.app.terms.ok') }}
                </v-btn>
            </template>
        </v-snackbar>

        <maintenance-overlay
            :is-active="appStore.isInMaintenanceMode"
            :message="appStore.maintenanceMessage"
            @attempt-reload="attemptReload"
        />

        <LegalDocumentsDialog
            v-if="termsUpdateDialog.show"
            :title="t('views.app.terms.updatedTitle')"
            :content="termsUpdateDialog.content"
            :version="termsUpdateDialog.version"
            :doc-type="userStore.termsDocType"
            :version-id="termsUpdateDialog.version"
            @close="onTermsDialogClosed"
        />

    </v-app>
</template>

<style>
html, body, #app {
  height: 100%;
  margin: 0;
  overflow: hidden;
}
.terms-update-snackbar .v-snackbar__content {
    text-align: center;
}

/* CRITICAL FIX FOR PRINTING FULL DOCUMENTS */
@media print {
    html, body, #app, .v-application, .v-application__wrap, .v-main {
        height: auto !important;
        overflow: visible !important;
        position: static !important;
    }
    .v-main {
        padding-top: 0 !important;
    }
    /* Hide scrollbars globally for print */
    ::-webkit-scrollbar {
        display: none !important;
    }
    * {
        scrollbar-width: none !important;
    }
}
</style>
